import { api } from './client';
import type { GenerateBody } from '@/lib/aiPipeline';

/**
 * Движок ИИ-заданий (D27): ручки `/admin/ielts/pipeline/*` speaking-service, только admin.
 * Договор — `docs/CONTRACTS/ai-task-pipeline.md`; поля — `internal/model/ai_pipeline_models.go`.
 *
 * ⚠️ ПУТИ — ЛИТЕРАЛАМИ, МЕТОДЫ — КАК В GO. `adminRoutesInGateway.test.ts` сверяет каждый
 * вызов с картой шлюза и маршрутами службы по методу: rating и правка промпта — PUT,
 * архив промпта — DELETE.
 */

export type AiItem = {
  id: string;
  skill: string;
  types: string[] | null;
  cefr_level: string;
  writing_kind?: string;
  writing_subtype?: string;
  /** Часть IELTS Listening 1–4 (29.09.2026); нет — не аудирование или элемент до частей. */
  listening_part?: number;
  text_id?: string;
  audio_id?: string;
  task_ids: string[] | null;
  writing_prompt_ids: string[] | null;
  speaking_prompt_ids: string[] | null;
  trigger: string;
  pipeline_state: string;
  /** true — обе проверки «прошло», false — хотя бы одна «не прошло», null — проверок ещё нет. */
  checks_passed: boolean | null;
  last_error?: string;
  review_status: string;
  review_comment?: string;
  reviewed_by?: string;
  reviewed_at?: string;
  /** Когда одобренное открыто ученикам; нет — ученик не видит, даже одобренное (29.09.2026). */
  published_at?: string | null;
  rating: number | null;
  rating_note?: string;
  rated_at?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
};

export type AiRun = {
  id: string;
  item_id: string;
  stage: string;
  attempt: number;
  status: string;
  error?: string;
  prompts: Record<string, unknown> | null;
  /** Ровно то, что ушло модели. */
  rendered: string;
  spec?: string;
  model: string;
  /** Ровно то, что вернулось. */
  raw_output: string;
  verdict: Record<string, unknown> | null;
  started_at: string;
  finished_at?: string;
};

export type AiQuestion = {
  id: string;
  ord: number;
  prompt: string;
  options?: string[] | null;
  answer_key: string;
  synonyms?: string[] | null;
  needs_review?: boolean;
  review_note?: string;
};

export type AiTask = {
  id: string;
  type: string;
  instruction?: string;
  group_label?: string;
  options?: string[] | null;
  review_status: string;
  /** По какой половине записи-части сделано задание (1–2); 0 или нет — до частей. */
  segment_ord?: number;
  questions?: AiQuestion[] | null;
};

export type AiWriting = { id: string; kind: string; subtype: string; prompt: string; review_status: string };
export type AiSpeaking = {
  id: string;
  part: number;
  topic?: string;
  cue_text?: string;
  questions?: string[] | null;
  review_status: string;
};

export type AiContent = {
  text?: { id: string; title?: string; body?: string; word_count?: number } | null;
  audio?: { id: string; title?: string; audio_url?: string; transcript?: string; duration_sec?: number } | null;
  tasks: AiTask[] | null;
  writing: AiWriting[] | null;
  speaking: AiSpeaking[] | null;
};

export type AiItemView = { item: AiItem; runs: AiRun[] | null; content: AiContent | null };

export type AiPromptVersion = {
  id: string;
  key: string;
  system: string;
  user_template: string;
  model: string;
  temperature: number;
  version: number;
  archived_at?: string;
  updated_by?: string;
  created_at: string;
};

export type AiPromptView = {
  key: string;
  /** Умолчание из кода. */
  system: string;
  user_template: string;
  temperature: number;
  required_placeholders: string[] | null;
  allowed_placeholders: string[] | null;
  /** null — действующей версии нет, этап берёт умолчание из кода. */
  current: AiPromptVersion | null;
};

export type AiPromptBody = { system: string; user_template: string; model: string; temperature: number | null };

const id_ = encodeURIComponent;

export const aiPipelineApi = {
  /** Заказ генерации → 202, элемент в `queued`; этапы идут фоном. */
  async generate(body: GenerateBody) {
    const { data } = await api.post('/admin/ielts/pipeline/generate', body);
    return data as AiItem;
  },
  async listItems(params: Record<string, string | number>) {
    const { data } = await api.get('/admin/ielts/pipeline/items', { params });
    return data as { items: AiItem[]; total: number };
  },
  async getItem(id: string) {
    const { data } = await api.get(`/admin/ielts/pipeline/items/${id_(id)}`);
    return data as AiItemView;
  },
  async rerun(id: string, stage: string) {
    const { data } = await api.post(`/admin/ielts/pipeline/items/${id_(id)}/stages/${id_(stage)}/rerun`);
    return data as AiItem;
  },
  /** `publish` — сразу открыть ученикам; без него одобренное ждёт `publish` (29.09.2026). */
  async approve(id: string, body: { comment: string; exclude_ids: string[]; publish: boolean }) {
    const { data } = await api.post(`/admin/ielts/pipeline/items/${id_(id)}/approve`, body);
    return data as AiItem;
  },
  /** Открыть одобренный элемент ученикам. Не одобрен — `pipeline_not_approved`. */
  async publish(id: string) {
    const { data } = await api.post(`/admin/ielts/pipeline/items/${id_(id)}/publish`);
    return data as AiItem;
  },
  /** Снять с публикации; одобрение остаётся. */
  async unpublish(id: string) {
    const { data } = await api.post(`/admin/ielts/pipeline/items/${id_(id)}/unpublish`);
    return data as AiItem;
  },
  async reject(id: string, comment: string) {
    const { data } = await api.post(`/admin/ielts/pipeline/items/${id_(id)}/reject`, { comment });
    return data as AiItem;
  },
  async rate(id: string, rating: number, note: string) {
    const { data } = await api.put(`/admin/ielts/pipeline/items/${id_(id)}/rating`, { rating, note });
    return data as AiItem;
  },
  async listPrompts() {
    const { data } = await api.get('/admin/ielts/pipeline/prompts');
    return ((data as { prompts?: AiPromptView[] }).prompts ?? []) as AiPromptView[];
  },
  /** Вся история ключа, новые первыми (в том числе архив). */
  async promptVersions(key: string) {
    const { data } = await api.get('/admin/ielts/pipeline/prompts', { params: { key } });
    return ((data as { versions?: AiPromptVersion[] }).versions ?? []) as AiPromptVersion[];
  },
  /** Первая версия — только если у ключа нет действующей (`prompt_exists`). */
  async createPrompt(body: AiPromptBody & { key: string }) {
    const { data } = await api.post('/admin/ielts/pipeline/prompts', body);
    return data as AiPromptVersion;
  },
  /** Новая версия; `version` — та, что правили. Не последняя → `prompt_stale`. */
  async updatePrompt(id: string, body: AiPromptBody & { version: number }) {
    const { data } = await api.put(`/admin/ielts/pipeline/prompts/${id_(id)}`, body);
    return data as AiPromptVersion;
  },
  /** В архив — этап берёт умолчание из кода. Удаления нет. */
  async archivePrompt(id: string) {
    const { data } = await api.delete(`/admin/ielts/pipeline/prompts/${id_(id)}`);
    return data as { id: string; archived: boolean };
  },
};
